import { FC, useEffect } from 'react';
import {
  Grid,
  TextField,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import StructuredModal from '../structuredModal/structuredModal';
import {
  TEventsPrimary,
  TUserData,
  TInstituteData,
} from '../../../apis/events/events.interfaces';
import ControlledSelectField from '../controlledComponents/ControlledSelectField.component';
import ControlledTextField from '../controlledComponents/ControlledTextField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';
import {
  isIsoDateRangeOrdered,
  zDateStringFromInput,
} from 'src/utils/zodFormHelpers';

interface AddEventComponentProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (event: Partial<TEventsPrimary>) => void;
  title: string;
  description: string;
  eventDetails: TEventsPrimary | null;
  isLoading: boolean;
  errorMessage: string;
}

type EventFormData = Omit<
  TEventsPrimary,
  | 'id'
  | 'manager'
  | 'plannedBy'
  | 'institute'
  | 'participants'
  | 'staffAssignments'
  | 'startDate'
  | 'endDate'
> & {
  managerId: string;
  plannedById: string;
  instituteId: string;
  startDate: string;
  endDate: string;
  relatedClass?: {
    grade: string;
    section: string;
  };
};

const getEventFormSchema = (translateText: TTranslateTextType) =>
  z
    .object({
      title: z
        .string()
        .min(1, translateText('events.validation.titleRequired'))
        .max(200, translateText('events.validation.titleMaxLength')),
      description: z
        .string()
        .min(1, translateText('events.validation.descriptionRequired')),
      startDate: zDateStringFromInput({
        requiredMessage: translateText('events.validation.startDateRequired'),
        message: translateText('common.dateInvalid') || undefined,
      }),
      endDate: zDateStringFromInput({
        requiredMessage: translateText('events.validation.endDateRequired'),
        message: translateText('common.dateInvalid') || undefined,
      }),
      eventGroup: z.enum([
        'exam',
        'sport',
        'cultural',
        'technical',
        'workshop',
        'seminar',
      ]),
      eventScope: z.enum(['student', 'class', 'staff', 'institute']),
      expenditureActual: z
        .number()
        .min(0, translateText('events.validation.expenditureNonNegative'))
        .optional(),
      expenditureEstimate: z
        .number()
        .min(0, translateText('events.validation.expenditureNonNegative'))
        .optional(),
      managerId: z
        .string()
        .min(1, translateText('events.validation.managerRequired')),
      plannedById: z
        .string()
        .min(1, translateText('events.validation.plannedByRequired')),
      instituteId: z
        .string()
        .min(1, translateText('events.validation.instituteRequired')),
      isMandatory: z.boolean(),
      priority: z.enum(['low', 'medium', 'high']),
      purpose: z
        .string()
        .min(1, translateText('events.validation.purposeRequired')),
      remarks: z.string().optional(),
      status: z.enum(['scheduled', 'completed', 'cancelled']),
      venue: z
        .string()
        .min(1, translateText('events.validation.venueRequired')),
      relatedClass: z
        .object({
          grade: z.string().optional(),
          section: z.string().optional(),
        })
        .optional(),
    })
    .refine(
      (data) => isIsoDateRangeOrdered(data.startDate, data.endDate),
      {
        message: translateText('events.validation.endDateAfterStart'),
        path: ['endDate'],
      }
    );

const defaultValues: EventFormData = {
  title: '',
  description: '',
  startDate: '',
  endDate: '',
  eventGroup: 'exam',
  eventScope: 'institute',
  expenditureActual: 0,
  expenditureEstimate: 0,
  managerId: '',
  plannedById: '',
  instituteId: '',
  isMandatory: false,
  priority: 'medium',
  purpose: '',
  remarks: '',
  status: 'scheduled',
  venue: '',
  relatedClass: undefined,
};

const AddEventComponent: FC<AddEventComponentProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  eventDetails,
  isLoading,
  errorMessage,
}) => {
  const { translateText } = useTranslation();
  const eventFormSchema = getEventFormSchema(translateText);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm<EventFormData>({
    resolver: zodResolver(eventFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  useEffect(() => {
    if (eventDetails) {
      reset({
        title: eventDetails.title || '',
        description: eventDetails.description || '',
        startDate: eventDetails.startDate
          ? new Date(eventDetails.startDate).toISOString().split('T')[0]
          : '',
        endDate: eventDetails.endDate
          ? new Date(eventDetails.endDate).toISOString().split('T')[0]
          : '',
        eventGroup: eventDetails.eventGroup || 'exam',
        eventScope: eventDetails.eventScope || 'institute',
        expenditureActual: eventDetails.expenditureActual || 0,
        expenditureEstimate: eventDetails.expenditureEstimate || 0,
        managerId: eventDetails.manager?._id || '',
        plannedById: eventDetails.plannedBy?._id || '',
        instituteId: eventDetails.institute?._id || '',
        isMandatory: eventDetails.isMandatory || false,
        priority: eventDetails.priority || 'medium',
        purpose: eventDetails.purpose || '',
        remarks: eventDetails.remarks || '',
        status: eventDetails.status || 'scheduled',
        venue: eventDetails.venue || '',
        relatedClass: eventDetails.relatedClass || undefined,
      });
    } else {
      reset(defaultValues);
    }
  }, [eventDetails, reset]);

  const onSubmitForm = (data: EventFormData) => {
    const eventData: Partial<TEventsPrimary> = {
      ...data,
      startDate: new Date(data.startDate),
      endDate: new Date(data.endDate),
      manager: { _id: data.managerId } as TUserData,
      plannedBy: { _id: data.plannedById } as TUserData,
      institute: { _id: data.instituteId } as TInstituteData,
      participants: [],
      staffAssignments: [],
    };
    onSubmit(eventData);
  };

  const isMandatory = watch('isMandatory');

  return (
    <StructuredModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footerPositiveAction={handleSubmit(onSubmitForm)}
      footerPositiveActionText={
        eventDetails
          ? translateText('events.update')
          : translateText('events.create')
      }
      footerNegativeAction={onClose}
      footerNegativeActionText={translateText('common.cancel')}
      footerMessage={
        errorMessage ? (
          <Typography color="error" variant="body2">
            {errorMessage}
          </Typography>
        ) : null
      }
    >
      <form onSubmit={handleSubmit(onSubmitForm)} style={{ width: '100%' }}>
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ p: { xs: 1, sm: 2 } }}
          data-testid="event-form-container"
        >
          {/* Basic Details Section */}
          <Grid item xs={12}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              {translateText('events.basicDetails')}
            </Typography>
            <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              id="title"
              label={translateText('events.title')}
              name="title"
              control={control}
              errorMessage={errors.title?.message}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={translateText('events.description')}
              multiline
              rows={4}
              {...register('description')}
              error={!!errors.description}
              helperText={errors.description?.message}
              required
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: { xs: '16px', sm: '14px' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={translateText('events.startDate')}
              type="date"
              InputLabelProps={{ shrink: true }}
              {...register('startDate')}
              error={!!errors.startDate}
              helperText={errors.startDate?.message}
              required
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: { xs: '44px', sm: '40px' },
                  fontSize: { xs: '16px', sm: '14px' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={translateText('events.endDate')}
              type="date"
              InputLabelProps={{ shrink: true }}
              {...register('endDate')}
              error={!!errors.endDate}
              helperText={errors.endDate?.message}
              required
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: { xs: '44px', sm: '40px' },
                  fontSize: { xs: '16px', sm: '14px' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              id="venue"
              label={translateText('events.venue')}
              name="venue"
              control={control}
              errorMessage={errors.venue?.message}
              required
            />
          </Grid>

          {/* Event Classification Section */}
          <Grid item xs={12} sx={{ mt: { xs: 1.5, sm: 2 } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              {translateText('events.eventClassification')}
            </Typography>
            <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledSelectField
              label={translateText('events.eventGroup')}
              id="eventGroup"
              name="eventGroup"
              control={control}
              isRequired
              itemList={[
                { key: 'exam', value: translateText('events.options.exam') },
                { key: 'sport', value: translateText('events.options.sport') },
                {
                  key: 'cultural',
                  value: translateText('events.options.cultural'),
                },
                {
                  key: 'technical',
                  value: translateText('events.options.technical'),
                },
                {
                  key: 'workshop',
                  value: translateText('events.options.workshop'),
                },
                {
                  key: 'seminar',
                  value: translateText('events.options.seminar'),
                },
              ]}
              errorMessage={errors.eventGroup?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledSelectField
              label={translateText('events.eventScope')}
              id="eventScope"
              name="eventScope"
              control={control}
              isRequired
              itemList={[
                {
                  key: 'student',
                  value: translateText('events.options.student'),
                },
                { key: 'class', value: translateText('events.options.class') },
                { key: 'staff', value: translateText('events.options.staff') },
                {
                  key: 'institute',
                  value: translateText('events.options.institute'),
                },
              ]}
              errorMessage={errors.eventScope?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledSelectField
              label={translateText('events.status')}
              id="status"
              name="status"
              control={control}
              isRequired
              itemList={[
                {
                  key: 'scheduled',
                  value: translateText('events.options.scheduled'),
                },
                {
                  key: 'completed',
                  value: translateText('events.options.completed'),
                },
                {
                  key: 'cancelled',
                  value: translateText('events.options.cancelled'),
                },
              ]}
              errorMessage={errors.status?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledSelectField
              label={translateText('events.priority')}
              id="priority"
              name="priority"
              control={control}
              isRequired
              itemList={[
                { key: 'low', value: translateText('events.options.low') },
                {
                  key: 'medium',
                  value: translateText('events.options.medium'),
                },
                { key: 'high', value: translateText('events.options.high') },
              ]}
              errorMessage={errors.priority?.message}
            />
          </Grid>

          {/* Financial Details Section */}
          <Grid item xs={12} sx={{ mt: { xs: 1.5, sm: 2 } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              {translateText('events.financialDetails')}
            </Typography>
            <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={translateText('events.estimatedExpenditure')}
              type="number"
              {...register('expenditureEstimate', { valueAsNumber: true })}
              error={!!errors.expenditureEstimate}
              helperText={errors.expenditureEstimate?.message}
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: { xs: '44px', sm: '40px' },
                  fontSize: { xs: '16px', sm: '14px' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={translateText('events.actualExpenditure')}
              type="number"
              {...register('expenditureActual', { valueAsNumber: true })}
              error={!!errors.expenditureActual}
              helperText={errors.expenditureActual?.message}
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: { xs: '44px', sm: '40px' },
                  fontSize: { xs: '16px', sm: '14px' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            />
          </Grid>

          {/* Assignment Details Section */}
          <Grid item xs={12} sx={{ mt: { xs: 1.5, sm: 2 } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              {translateText('events.assignmentDetails')}
            </Typography>
            <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledTextField
              id="managerId"
              label={translateText('events.managerId')}
              name="managerId"
              control={control}
              errorMessage={errors.managerId?.message}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledTextField
              id="plannedById"
              label={translateText('events.plannedById')}
              name="plannedById"
              control={control}
              errorMessage={errors.plannedById?.message}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              id="instituteId"
              label={translateText('events.instituteId')}
              name="instituteId"
              control={control}
              errorMessage={errors.instituteId?.message}
              required
            />
          </Grid>

          {/* Additional Details Section */}
          <Grid item xs={12} sx={{ mt: { xs: 1.5, sm: 2 } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              {translateText('events.additionalDetails')}
            </Typography>
            <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              id="purpose"
              label={translateText('events.purpose')}
              name="purpose"
              control={control}
              errorMessage={errors.purpose?.message}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={translateText('events.remarks')}
              multiline
              rows={3}
              {...register('remarks')}
              error={!!errors.remarks}
              helperText={errors.remarks?.message}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: { xs: '16px', sm: '14px' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  {...register('isMandatory')}
                  checked={isMandatory}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: { xs: '1.5rem', sm: '1.25rem' },
                    },
                  }}
                />
              }
              label={translateText('events.isMandatory')}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            />
          </Grid>

          {/* Related Class Section (Optional) */}
          <Grid item xs={12} sx={{ mt: { xs: 1.5, sm: 2 } }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              {translateText('events.relatedClass')}
            </Typography>
            <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={translateText('events.grade')}
              {...register('relatedClass.grade')}
              error={!!errors.relatedClass?.grade}
              helperText={errors.relatedClass?.grade?.message}
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: { xs: '44px', sm: '40px' },
                  fontSize: { xs: '16px', sm: '14px' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={translateText('events.section')}
              {...register('relatedClass.section')}
              error={!!errors.relatedClass?.section}
              helperText={errors.relatedClass?.section?.message}
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: { xs: '44px', sm: '40px' },
                  fontSize: { xs: '16px', sm: '14px' },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                },
              }}
            />
          </Grid>
        </Grid>
      </form>
    </StructuredModal>
  );
};

export default AddEventComponent;
